import React from 'react';
import { Link } from 'react-router-dom';
import { DollarSign, Zap, Award, Maximize, ArrowRight, GraduationCap, Briefcase, Check, X } from 'lucide-react';
import { benefits } from '../data/mock';

const iconMap = { DollarSign, Zap, Award, Maximize };

export default function WhyFractionalPage() {
  return (
    <div>
      {/* Page Header */}
      <section className="hero-gradient pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6" data-testid="why-fractional-title">
            Why Fractional Leadership?
          </h1>
          <p className="text-lg sm:text-xl text-white/75 max-w-2xl mx-auto leading-relaxed">
            Get the same strategic impact without the overhead, risk, or lengthy hiring process.
          </p>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 lg:py-28 bg-background px-4 sm:px-6 lg:px-8" data-testid="comparison-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground text-center mb-16">
            Full-Time vs <span className="gold-underline">Fractional</span>
          </h2>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-20">
            {/* Full-Time Challenge */}
            <div className="card-depth rounded-2xl p-8 lg:p-10 border-red-200 bg-red-50/50" data-testid="comparison-fulltime">
              <h3 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-3">
                <X className="w-6 h-6 text-red-500" />
                The Full-Time Challenge
              </h3>
              <ul className="space-y-4">
                {[
                  '$250K-$400K+ annual cost',
                  '4-6 months to hire',
                  'High risk if wrong fit',
                  'Significant equity dilution',
                  'May be over-leveled for stage',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-muted-foreground">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-400 mt-2 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Fractional Advantage */}
            <div className="card-depth rounded-2xl p-8 lg:p-10 border-accent/30 bg-accent/5" data-testid="comparison-fractional">
              <h3 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-3">
                <Check className="w-6 h-6 text-accent" />
                The Fractional Advantage
              </h3>
              <ul className="space-y-4">
                {[
                  '$100K-$150K annual cost',
                  '1-2 weeks to start',
                  'Low risk, flexible terms',
                  'No equity required',
                  'Right-sized for your stage',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-muted-foreground">
                    <Check className="w-4 h-4 text-accent flex-shrink-0 mt-1" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-20 lg:py-28 bg-primary/5 px-4 sm:px-6 lg:px-8 overflow-hidden" data-testid="audience-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-12">
                Designed for <span className="gold-underline">startup momentum</span>
              </h2>
              <div className="space-y-8">
                <div className="flex gap-5">
                  <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-accent/10 flex items-center justify-center">
                    <GraduationCap className="w-7 h-7 text-accent" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-foreground mb-2">Bootstrapped to Series B</p>
                    <p className="text-muted-foreground leading-relaxed">Tech founders who need senior sales leadership but can't justify a $300K+ hire yet.</p>
                  </div>
                </div>
                <div className="flex gap-5">
                  <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-accent/10 flex items-center justify-center">
                    <Briefcase className="w-7 h-7 text-accent" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-foreground mb-2">B2C to B2B transitions</p>
                    <p className="text-muted-foreground leading-relaxed">SaaS companies pivoting or expanding to B2B who need enterprise sales expertise fast.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="animate-float">
                <img
                  src="https://images.pexels.com/photos/194094/pexels-photo-194094.jpeg"
                  alt="Leadership growth"
                  className="w-full h-auto rounded-2xl shadow-2xl shadow-primary/20"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Grid */}
      <section className="py-20 lg:py-28 bg-background px-4 sm:px-6 lg:px-8" data-testid="benefits-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground text-center mb-16">
            The numbers speak for themselves
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit) => {
              const Icon = iconMap[benefit.icon] || DollarSign;
              return (
                <div key={benefit.id} className="card-depth rounded-2xl p-8 text-center" data-testid={`benefit-card-${benefit.id}`}>
                  <Icon className="w-10 h-10 text-primary mx-auto mb-4" />
                  <div className="text-2xl font-bold text-accent mb-2">{benefit.metric}</div>
                  <h3 className="text-lg font-bold text-foreground mb-2">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 lg:py-32 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="absolute inset-0 cta-gradient animate-gradient" />
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-8">
            See how much you can save
          </h2>
          <Link
            to="/roi-calculator"
            data-testid="why-fractional-roi-btn"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-accent text-white font-semibold text-lg px-10 py-4 hover:bg-accent/90 hover-lift group"
          >
            Try ROI Calculator
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </div>
  );
}
